import { Box, Flex, IconButton, Th } from "@chakra-ui/react"
import type * as CSS from "csstype"
import { useSearchParams } from "react-router-dom"

import { AngleDownIcon, AngleUpIcon, FilterIcon } from "@/components/Icons"
import { Order, Orders } from "@/utils/filters/types"
import { isNotBlank, isNotNullNorUndefined, isNullOrUndefined } from "@/utils/ts-utils"

type Props = React.PropsWithChildren<{
  sortList?: string[]
  sortParameter?: string
  align?: CSS.Property.JustifyContent
  px?: CSS.Property.Padding | number
}>

export default ({ children, sortList, sortParameter, align, px }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const canSort = isNotNullNorUndefined(sortParameter)
  const currentSort = canSort ? searchParams.get(sortParameter) : undefined
  const isSortingByThisColumn = isNotNullNorUndefined(currentSort)

  const toggleSortButtonClick = () => {
    const searchParamsNew = new URLSearchParams(searchParams)

    const [sortA, sortB] = (() => {
      if (isNullOrUndefined(sortList)) return [Orders.ASC, Orders.DESC]
      const sort0 = Order.includes(sortList?.[0] as Order) ? (sortList?.[0] as Order) : Orders.ASC
      if (sortList.length < 2) return [sort0, undefined]
      const sort1 = Order.includes(sortList?.[1] as Order) ? (sortList?.[1] as Order) : Orders.DESC
      return [sort0, sort1]
    })()

    const next = !isSortingByThisColumn ? sortA : currentSort === sortA ? sortB : undefined

    if (isNotBlank(sortParameter)) {
      if (isNullOrUndefined(next)) {
        searchParamsNew.delete(sortParameter)
      } else {
        searchParamsNew.set(sortParameter, next)
      }
      setSearchParams(searchParamsNew)
    }
  }

  // Design system table header (table.css): the sorted column turns black, never red; the
  // direction is an arrow, and the header keeps the th typography.
  const ariaSort = isSortingByThisColumn
    ? currentSort === Orders.ASC
      ? "ascending"
      : "descending"
    : undefined

  return (
    <Th px={px} aria-sort={ariaSort} color={isSortingByThisColumn ? "text" : undefined}>
      <Flex alignItems="center" justifyContent={align} gap="1">
        <Box as="span">{children}</Box>
        {canSort && (
          <IconButton
            aria-label="sort button"
            variant="ghost"
            size="sm"
            h="6"
            w="6"
            minH="0"
            minW="0"
            p="0"
            flexShrink="0"
            borderRadius="sm"
            color={isSortingByThisColumn ? "text" : "textSubtle"}
            onClick={toggleSortButtonClick}
          >
            <FilterIcon h="4" w="4" />
          </IconButton>
        )}
        {canSort && isSortingByThisColumn ? (
          currentSort === Orders.ASC ? (
            <AngleUpIcon boxSize={3} aria-hidden />
          ) : (
            <AngleDownIcon boxSize={3} aria-hidden />
          )
        ) : null}
      </Flex>
    </Th>
  )
}
